import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useThemeMode } from "../components/ThemeMode";
import { COMPANY, Icon, ICONS, SectionLink, SiteFooter, SiteNav, useReveal } from "../components/site/SiteChrome";
import InquiryForm from "../components/site/InquiryForm";
import "./LandingPage.css";

/* ─── Hero: an animated test run in which one step is healed ─── */

const DEMO_STEPS = [
	{ type: "navigate", label: "Open", target: "shop.acme.io/login" },
	{ type: "input", label: "Type", target: "Email → qa@acme.io" },
	{ type: "input", label: "Type", target: "Password → ••••••••" },
	{ type: "click", label: "Click", target: "“Sign in”" },
	{
		type: "click",
		label: "Click",
		target: "“Add to cart”",
		heal: { from: "#btn-add-2931", to: '[data-testid="add-to-cart"]' },
	},
	{ type: "assert", label: "Assert", target: "text “Order confirmed”" },
];
const STEP_MS = 900;
const HOLD_TICKS = 5;

function RunDemo() {
	const [tick, setTick] = useState(0);
	const total = DEMO_STEPS.length;

	useEffect(() => {
		const id = setInterval(() => setTick((t) => (t + 1) % (total + HOLD_TICKS + 1)), STEP_MS);
		return () => clearInterval(id);
	}, [total]);

	// Runs with reduced motion too: steps change, nothing moves (see LandingPage.css)
	const current = tick;
	const done = Math.min(current, total);
	const finished = current >= total;
	const healedShown = DEMO_STEPS.some((s, i) => s.heal && i < current);

	return (
		<div className="run-window">
			<div className="run-chrome">
				<div className="run-dots">
					<span />
					<span />
					<span />
				</div>
				<div className="run-url">testify · Checkout regression</div>
				<div className={`run-status${finished ? " ok" : ""}`}>
					<span className="run-status-dot" />
					{finished ? "Passed" : "Running"}
				</div>
			</div>
			<div className="run-body">
				<div className="run-progress">
					<div className="run-progress-bar" style={{ width: `${(done / total) * 100}%` }} />
				</div>
				<ol className="run-steps">
					{DEMO_STEPS.map((s, i) => {
						const state = i < current ? "passed" : i === current ? "running" : "pending";
						return (
							<li key={i} className={`run-step ${state}`}>
								<span className="run-step-icon">
									{state === "passed" ? (
										<Icon d={ICONS.check} size={13} stroke={3} />
									) : state === "running" ? (
										<span className="spinner" />
									) : (
										i + 1
									)}
								</span>
								<span className="run-step-text">
									<b>{s.label}</b> {s.target}
								</span>
								{s.heal && state !== "pending" && (
									<span className={`chip chip-heal${state === "passed" ? " show" : ""}`}>
										<Icon d={ICONS.sparkle} size={12} /> Healed
									</span>
								)}
								{s.heal && state === "running" && <span className="chip chip-warn">Selector not found…</span>}
							</li>
						);
					})}
				</ol>
				<div className={`run-heal-card${healedShown ? " show" : ""}`}>
					<div className="run-heal-title">
						<Icon d={ICONS.sparkle} size={14} /> Locator healed via fallback · pending review
					</div>
					<code className="sel-old">#btn-add-2931</code>
					<Icon d={ICONS.arrow} size={14} />
					<code className="sel-new">[data-testid="add-to-cart"]</code>
				</div>
			</div>
		</div>
	);
}

/* ─── Small building blocks ─── */

function FaqItem({ question, answer }) {
	const [open, setOpen] = useState(false);
	return (
		<div className={`faq-item${open ? " open" : ""}`}>
			<button className="faq-question" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
				<span>{question}</span>
				<span className="faq-plus" aria-hidden="true" />
			</button>
			<div className="faq-answer-wrap">
				<div className="faq-answer-inner">
					<p className="faq-answer">{answer}</p>
				</div>
			</div>
		</div>
	);
}

const HEAL_CHAIN = [
	{
		title: "Primary selector",
		body: "The selector you recorded. If it matches several elements, Testify narrows them by enabled state and the recorded text.",
	},
	{
		title: "Ranked fallback locators",
		body: "Up to 8 locators captured at record time — test ids, id, name, role, text, CSS, XPath — retried by score for 10 seconds.",
	},
	{
		title: "Similarity match",
		body: "Tag, text, attributes and placeholder are compared against the element's recorded fingerprint.",
	},
	{
		title: "AI locate",
		body: "A pruned DOM snapshot goes to an LLM, which proposes a selector with a confidence score and a reason.",
	},
	{
		title: "You stay in control",
		body: "Every heal is logged with the old and new selector. Apply it to the test in one click — or reject it.",
		final: true,
	},
];

const FEATURES = [
	{
		icon: ICONS.record,
		title: "Chrome recorder",
		body: "Clicks, typing, drag-and-drop, hovers, scrolls, new tabs and popups become readable steps. Right-click any element to add an assertion.",
		wide: true,
		visual: "recorder",
	},
	{
		icon: ICONS.replay,
		title: "Replay, then re-record",
		body: "Fix one step without starting over: Testify replays the test up to it in your browser, then records or lets you pick the element.",
	},
	{
		icon: ICONS.data,
		title: "Data-driven runs",
		body: "Bind a test to a data set and every row becomes its own run, with {{variables}} filled in.",
	},
	{
		icon: ICONS.layers,
		title: "Suites, loops & parallel runs",
		body: "Group tests into suites, run them one by one or in parallel, loop a test up to 20×, and rerun only what failed. A fair queue keeps every team moving.",
		wide: true,
		visual: "lanes",
	},
	{
		icon: ICONS.clock,
		title: "Schedules",
		body: "One-off or recurring — every few minutes, daily, weekly or monthly.",
	},
	{
		icon: ICONS.live,
		title: "Live execution",
		body: "Watch each step stream in with screenshots, and stop any run mid-flight.",
	},
	{
		icon: ICONS.shield,
		title: "Smart action recovery",
		body: "Overlay in the way? Hidden checkbox? Testify presses Escape or clicks the label — and tells you what it did.",
	},
	{
		icon: ICONS.users,
		title: "Built for teams",
		body: "Each organisation gets its own isolated database. Admin, Super user and User roles, view-only sharing, and one-click clone of anyone's test.",
		wide: true,
		visual: "roles",
	},
	{
		icon: ICONS.chart,
		title: "Analytics",
		body: "Pass rates, run history and every heal event, in one view.",
	},
	{
		icon: ICONS.api,
		title: "API testing",
		body: "Test your APIs without a browser. Chain requests, save values like a login token for the next one, and check status, headers, JSON fields and response time.",
		visual: "api",
	},
	{
		icon: ICONS.pipeline,
		title: "CI/CD integration",
		body: "Run a suite from GitHub Actions, GitLab CI, Jenkins or Azure DevOps with one command. Personal API tokens, a JUnit report for your pipeline, and a failing test fails the build.",
		wide: true,
		visual: "pipeline",
	},
];

const FAQS = [
	{
		q: "Do I need to know how to code?",
		a: "No. Testify is built for QA engineers, product managers and business analysts. The Chrome extension records what you do; Testify turns it into a test and runs it.",
	},
	{
		q: "How does self-healing work?",
		a: "When the recorded selector stops matching, Testify works through the locators it captured at record time, then a similarity match against the element's fingerprint, then an AI lookup. Every heal is saved for review, so you decide whether it becomes part of the test.",
	},
	{
		q: "Which browsers are supported?",
		a: "You record in Chrome. Tests run in real browsers: Chromium by default, and suites can also run in Firefox or WebKit.",
	},
	{
		q: "Can I edit a test after recording it?",
		a: "Yes. Edit, reorder, duplicate or disable any step, or let Testify replay the test up to a step in your browser and record from there — without re-recording the whole flow.",
	},
	{
		q: "Can I run tests from our CI/CD pipeline?",
		a: "Yes. Create an API token in Testify, download the command-line tool from your Testify server, and add one step to your pipeline. It starts a suite, waits for the result, writes a JUnit report and fails the build if a test fails. Ready-made snippets cover GitHub Actions, GitLab CI, Jenkins and Azure DevOps.",
	},
	{
		q: "Can Testify test APIs as well as web pages?",
		a: "Yes. API tests send HTTP requests without opening a browser. Each request can check the status, headers, JSON fields and response time, and save values for the requests after it. They run in the same suites, schedules and pipelines as your browser tests.",
	},
	{
		q: "Is it suitable for larger teams?",
		a: "Yes. Each organisation's data is kept in its own database, with Admin, Super user and User roles, per-user data visibility, a shared run queue with fair-share policies, and suite-level reports.",
	},
	{
		q: "How is Testify different from Selenium or Cypress?",
		a: "Selenium and Cypress are code frameworks — you write and maintain tests in JavaScript, Python or Java. Testify records the test for you, runs it in a real browser and repairs it when your UI changes. There's no code to write.",
	},
	{
		q: "How do I get an account?",
		a: "Accounts are set up for your organisation by our team. Request a demo below and we'll get you started.",
	},
];

/* ─── Page ─── */

export default function LandingPage() {
	const navigate = useNavigate();
	const location = useLocation();
	const { isAuthenticated } = useSelector((state) => state.auth);
	const { mode } = useThemeMode();

	useReveal();

	const handleCta = () => navigate(isAuthenticated ? "/dashboard" : "/login");
	const primaryLabel = isAuthenticated ? "Open App" : "Sign in";

	useEffect(() => {
		document.title = "Testify — No-Code Test Automation That Heals Itself";
		return () => {
			document.title = "Testify";
		};
	}, []);

	// Arriving from another page with a section in the URL (/#demo): the browser tried to scroll
	// before the page rendered, so scroll now.
	useEffect(() => {
		if (!location.hash) return;
		const id = requestAnimationFrame(() => document.getElementById(location.hash.slice(1))?.scrollIntoView());
		return () => cancelAnimationFrame(id);
	}, [location.hash]);

	return (
		<div className="lp" data-theme={mode}>
			<SiteNav />

			{/* HERO */}
			<header className="hero">
				<div className="hero-bg" aria-hidden="true">
					<div className="blob blob-1" />
					<div className="blob blob-2" />
					<div className="blob blob-3" />
					<div className="hero-grid" />
				</div>
				<div className="container hero-inner">
					<div className="hero-copy">
						<a href="#healing" className="pill">
							<span className="pill-tag">New</span>
							AI self-healing, reviewed by you
							<Icon d={ICONS.arrow} size={14} />
						</a>
						<h1>
							Test automation
							<br />
							that <span className="grad-text">heals itself.</span>
						</h1>
						<p className="hero-lead">
							Record browser tests in one click. Testify runs them in real browsers and repairs broken selectors
							when your UI changes — no code, no flaky Mondays.
						</p>
						<div className="cta-row">
							<button className="btn btn-primary btn-lg" onClick={handleCta}>
								{primaryLabel}
								<Icon d={ICONS.arrow} size={18} />
							</button>
							<SectionLink hash="#demo" className="btn btn-outline btn-lg">
								Request a demo
							</SectionLink>
						</div>
						<ul className="hero-ticks">
							<li><Icon d={ICONS.check} size={15} stroke={3} /> Zero lines of code</li>
							<li><Icon d={ICONS.check} size={15} stroke={3} /> Runs in real browsers</li>
							<li><Icon d={ICONS.check} size={15} stroke={3} /> Every heal reviewable</li>
						</ul>
					</div>

					<div className="hero-visual">
						<div className="hero-visual-glow" aria-hidden="true" />
						<RunDemo />
						<div className="float-card float-a">
							<div className="float-icon green">
								<Icon d={ICONS.check} size={16} stroke={3} />
							</div>
							<div>
								<div className="float-title">Nightly regression</div>
								<div className="float-sub">48 / 48 tests passed</div>
							</div>
						</div>
						<div className="float-card float-b">
							<div className="float-icon violet">
								<Icon d={ICONS.sparkle} size={16} />
							</div>
							<div>
								<div className="float-title">3 heals this week</div>
								<div className="float-sub">0 tests rewritten by hand</div>
							</div>
						</div>
					</div>
				</div>

				<div className="container">
					<div className="engines" data-reveal>
						<span className="engines-label">Works with</span>
						{["Chrome extension recorder", "Chromium", "Firefox", "WebKit"].map((n) => (
							<span key={n} className="engine">
								{n}
							</span>
						))}
					</div>
				</div>
			</header>

			{/* PROBLEM */}
			<section className="section">
				<div className="container">
					<div className="section-head" data-reveal>
						<span className="eyebrow">The problem</span>
						<h2>
							Your tests break. You fix them.
							<br />
							<span className="muted">Repeat, every sprint.</span>
						</h2>
						<p className="section-lead">
							QA teams spend most of their time repairing tests instead of writing new ones. That isn't automation
							— it's maintenance.
						</p>
					</div>
					<div className="pain-grid">
						{[
							{
								n: "01",
								title: "Selectors break constantly",
								body: "A renamed class or a regenerated ID turns the pipeline red, and someone loses a day hunting for the new one.",
							},
							{
								n: "02",
								title: "Automation needs engineers",
								body: "Scripting in Python or JavaScript keeps QA analysts, PMs and business users out of test automation.",
							},
							{
								n: "03",
								title: "Enterprise tools cost a fortune",
								body: "Heavyweight platforms charge thousands a month, and budget approval takes longer than the rollout.",
							},
						].map(({ n, title, body }, i) => (
							<div key={n} className="pain-card" data-reveal style={{ transitionDelay: `${i * 80}ms` }}>
								<span className="pain-n">{n}</span>
								<h3>{title}</h3>
								<p>{body}</p>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* HOW IT WORKS */}
			<section className="section section-alt" id="how-it-works">
				<div className="container">
					<div className="section-head" data-reveal>
						<span className="eyebrow">How it works</span>
						<h2>From zero to automated in minutes</h2>
						<p className="section-lead">No scripts, no config files, no DevOps. Install the extension and press record.</p>
					</div>
					<div className="steps">
						{[
							{
								title: "Record",
								body: "Browse your app as a normal user. Every click, keystroke and navigation is captured with up to 8 ranked locators per element.",
								tag: "Chrome extension",
							},
							{
								title: "Run",
								body: "Execute on demand, in suites, in parallel, on a schedule, across a data set or from your CI/CD pipeline — in real browsers.",
								tag: "Real browsers",
							},
							{
								title: "Heal",
								body: "When the UI changes, Testify finds the element anyway, keeps the run green, and queues the fix for your review.",
								tag: "AI self-healing",
							},
						].map(({ title, body, tag }, i) => (
							<div key={title} className="step" data-reveal style={{ transitionDelay: `${i * 100}ms` }}>
								<div className="step-num">{i + 1}</div>
								<h3>{title}</h3>
								<p>{body}</p>
								<span className="step-tag">{tag}</span>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* SELF-HEALING */}
			<section className="section" id="healing">
				<div className="container heal-grid">
					<div className="heal-copy" data-reveal>
						<span className="eyebrow">Self-healing</span>
						<h2>
							Four ways to find it.
							<br />
							<span className="grad-text">One click to approve.</span>
						</h2>
						<p className="section-lead left">
							Testify records a full fingerprint of every element and works through a layered chain before it
							gives up — then shows you exactly what changed.
						</p>
						<div className="heal-diff">
							<div className="heal-diff-head">
								<span className="chip chip-heal show">
									<Icon d={ICONS.sparkle} size={12} /> Heal · similarity · 92%
								</span>
								<span className="heal-diff-step">Step 7 · Click “Checkout”</span>
							</div>
							<div className="diff-line del">
								<span>−</span>
								<code>div.cart-footer &gt; button:nth-child(2)</code>
							</div>
							<div className="diff-line add">
								<span>+</span>
								<code>role=button[name="Checkout"]</code>
							</div>
							<div className="heal-diff-actions">
								<span className="fake-btn primary">Apply</span>
								<span className="fake-btn">Reject</span>
							</div>
						</div>
					</div>
					<ol className="chain">
						{HEAL_CHAIN.map(({ title, body, final }, i) => (
							<li
								key={title}
								className={`chain-item${final ? " final" : ""}`}
								data-reveal
								style={{ transitionDelay: `${i * 90}ms` }}
							>
								<span className="chain-dot">{final ? <Icon d={ICONS.check} size={14} stroke={3} /> : i + 1}</span>
								<div>
									<h3>{title}</h3>
									<p>{body}</p>
								</div>
							</li>
						))}
					</ol>
				</div>
			</section>

			{/* FEATURES */}
			<section className="section section-alt" id="features">
				<div className="container">
					<div className="section-head" data-reveal>
						<span className="eyebrow">Features</span>
						<h2>
							Everything your QA team needs.
							<br />
							<span className="muted">Nothing your developers hate.</span>
						</h2>
					</div>
					<div className="bento">
						{FEATURES.map(({ icon, title, body, wide, visual }, i) => (
							<div
								key={title}
								className={`bento-card${wide ? " wide" : ""}`}
								data-reveal
								style={{ transitionDelay: `${(i % 3) * 70}ms` }}
							>
								<div className="bento-icon">
									<Icon d={icon} size={20} />
								</div>
								<h3>{title}</h3>
								<p>{body}</p>
								{visual === "recorder" && (
									<div className="viz-rec">
										<span className="rec-dot" /> Recording
										<div className="viz-rec-steps">
											<span className="mini-step">Click “Pricing”</span>
											<span className="mini-step">Type “team@acme.io”</span>
											<span className="mini-step assert">Assert URL contains /checkout</span>
										</div>
									</div>
								)}
								{visual === "lanes" && (
									<div className="viz-lanes">
										{[72, 100, 45, 88].map((w, j) => (
											<div key={j} className="lane">
												<span className="lane-label">Run {j + 1}</span>
												<span className="lane-track">
													<span className="lane-bar" style={{ "--w": `${w}%`, animationDelay: `${j * 0.4}s` }} />
												</span>
											</div>
										))}
									</div>
								)}
								{visual === "api" && (
									<div className="viz-api">
										{[
											{ method: "POST", path: "/login", note: "saves {{token}}" },
											{ method: "GET", path: "/orders", note: "$.total > 0" },
										].map(({ method, path, note }) => (
											<div key={path} className="api-row">
												<span className={`api-method m-${method.toLowerCase()}`}>{method}</span>
												<code>{path}</code>
												<span className="api-note">{note}</span>
												<span className="api-status">200</span>
											</div>
										))}
									</div>
								)}
								{visual === "pipeline" && (
									<div className="viz-term" aria-hidden="true">
										<div className="term-line">
											<span className="term-prompt">$</span> node testify.mjs run --suite "Smoke" --junit report.xml
										</div>
										{[
											["Sign in", "1.8s"],
											["Checkout", "4.2s"],
											["Orders API", "0.6s"],
										].map(([name, time]) => (
											<div key={name} className="term-line pass">
												<span>✓ {name}</span>
												<span className="term-time">{time}</span>
											</div>
										))}
										<div className="term-line summary">3 passed · report.xml written · exit 0</div>
									</div>
								)}
								{visual === "roles" && (
									<div className="viz-roles">
										<span className="role r-admin">Admin</span>
										<span className="role r-super">Super user</span>
										<span className="role r-user">User</span>
										<span className="role r-view">View only</span>
									</div>
								)}
							</div>
						))}
					</div>
				</div>
			</section>

			{/* STATS */}
			<section className="stats">
				<div className="container stats-grid">
					{[
						{ n: "0", label: "lines of code to write" },
						{ n: "8", label: "ranked locators per element" },
						{ n: "4", label: "layers of element recovery" },
						{ n: "3", label: "browser engines supported" },
					].map(({ n, label }, i) => (
						<div key={label} className="stat" data-reveal style={{ transitionDelay: `${i * 80}ms` }}>
							<div className="stat-n">{n}</div>
							<div className="stat-label">{label}</div>
						</div>
					))}
				</div>
			</section>

			{/* FAQ */}
			<section className="section section-alt" id="faq">
				<div className="container faq-grid">
					<div className="faq-head" data-reveal>
						<span className="eyebrow">FAQ</span>
						<h2>Questions, answered.</h2>
						<p className="section-lead left">
							Something else on your mind?{" "}
							<Link className="link" to="/contact">
								Get in touch
							</Link>
							.
						</p>
					</div>
					<div className="faq-list" data-reveal>
						{FAQS.map(({ q, a }) => (
							<FaqItem key={q} question={q} answer={a} />
						))}
					</div>
				</div>
			</section>

			{/* DEMO REQUEST */}
			<section className="section cta-section" id="demo">
				<div className="container">
					<div className="cta-card demo-card" data-reveal>
						<div className="cta-card-bg" aria-hidden="true" />
						<div className="demo-grid">
							<div className="demo-copy">
								<span className="eyebrow">Book a demo</span>
								<h2>
									Stop fixing tests.
									<br />
									Start shipping.
								</h2>
								<p>See Testify record, run and heal a test on your own app — without writing a line of code.</p>
								<ul className="demo-points">
									{[
										"Record and run a test on your own app",
										"Watch a broken selector heal — and how you review it",
										"Get your organisation's accounts set up",
									].map((point) => (
										<li key={point}>
											<Icon d={ICONS.check} size={15} stroke={3} /> {point}
										</li>
									))}
								</ul>
								<p className="demo-alt">
									Prefer to talk? Call <a href={COMPANY.phoneHref}>{COMPANY.phone}</a> or{" "}
									<Link to="/contact">send us a message</Link>.
								</p>
							</div>
							<div className="demo-form-panel">
								<InquiryForm type="DEMO" />
							</div>
						</div>
					</div>
				</div>
			</section>

			<SiteFooter />
		</div>
	);
}
